import { useState, forwardRef, useImperativeHandle } from 'react';
import {
  Flex,
  Text,
  Box,
  Button,
  useColorModeValue,
  Link,
  Skeleton,
  SkeletonText,
  Alert,
  AlertIcon,
  Divider,
  IconButton,
  Icon,
} from '@chakra-ui/react';
import {
  PhoneIcon,
  EditIcon,
  ExternalLinkIcon,
  CloseIcon,
  RepeatIcon,
} from '@chakra-ui/icons';
import { FaFloppyDisk } from 'react-icons/fa6';

import CustomerForm, { type CustomerFormData } from './CustomerForm';
import { COMPANY_NAME, formatDate } from '../constants';
import { getMissingFieldsSet } from '../utils/validateCustomer';

import type { APIActurisCustomerDetailData } from 'types/api.types';

export interface ClientCardHandle {
  startEditing: () => void;
}

interface ClientCardProps {
  customer: APIActurisCustomerDetailData | undefined;
  isLoading: boolean;
  error: Error | null;
  onUpdate: (data: Partial<APIActurisCustomerDetailData>) => void;
  isUpdating: boolean;
}

const getFormDataFromCustomer = (
  customer: APIActurisCustomerDetailData,
): CustomerFormData => ({
  first_name: customer.first_name,
  last_name: customer.last_name,
  date_of_birth: customer.date_of_birth,
  email: customer.email,
  home_phone: customer.home_phone || '',
  mobile_phone: customer.mobile_phone || '',
  street_address: customer.street_address || '',
  city: customer.city || '',
  province: customer.province,
  postal_code: customer.postal_code || '',
});

const ClientCard = forwardRef<ClientCardHandle, ClientCardProps>(
  ({ customer, isLoading, error, onUpdate, isUpdating }, ref) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState<CustomerFormData | null>(null);

    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const labelColor = useColorModeValue('gray.500', 'gray.400');
    const cardBg = useColorModeValue('white', 'navy.800');
    const missingColor = useColorModeValue('red.500', 'red.300');

    const missingFields = getMissingFieldsSet(customer);

    const handleEditClick = () => {
      if (!customer) return;
      setEditData(getFormDataFromCustomer(customer));
      setIsEditing(true);
    };

    useImperativeHandle(ref, () => ({
      startEditing: handleEditClick,
    }));

    const handleCancelEdit = () => {
      setIsEditing(false);
      setEditData(null);
    };

    const handleResetEdit = () => {
      if (!customer) return;
      setEditData(getFormDataFromCustomer(customer));
    };

    const handleSaveEdit = () => {
      if (!editData) return;
      onUpdate(editData);
      setIsEditing(false);
      setEditData(null);
    };

    const renderContent = () => {
      if (isLoading) {
        return (
          <Box p={5}>
            <Flex mb={3} gap={4}>
              <Skeleton height="20px" width="120px" />
              <Skeleton height="20px" width="100px" />
            </Flex>
            <SkeletonText noOfLines={5} spacing={4} skeletonHeight="14px" />
            <Flex direction="column" gap={3} mt={6}>
              <Skeleton height="32px" borderRadius="md" />
              <Skeleton height="32px" borderRadius="md" />
            </Flex>
          </Box>
        );
      }

      if (error) {
        return (
          <Box p={5}>
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              Failed to load customer
            </Alert>
          </Box>
        );
      }

      if (!customer) {
        return (
          <Box p={5}>
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              Customer not found
            </Alert>
          </Box>
        );
      }

      if (isEditing && editData) {
        return (
          <Box p={5} maxH="400px" overflowY="auto">
            <CustomerForm data={editData} onChange={setEditData} />
          </Box>
        );
      }

      const hasNameMissing =
        missingFields.has('first_name') || missingFields.has('last_name');
      const hasAddressMissing =
        missingFields.has('street_address') ||
        missingFields.has('city') ||
        missingFields.has('province') ||
        missingFields.has('postal_code');

      return (
        <Box p={5}>
          <Flex mb={3}>
            <Flex flex={1}>
              <Text color={labelColor} fontSize="sm" minW="50px">
                Client:
              </Text>
              <Text
                color={hasNameMissing ? missingColor : textColor}
                fontSize="sm"
                fontWeight="500"
              >
                {customer.full_name || 'Missing'}
              </Text>
            </Flex>
            <Flex flex={1} justify="end">
              <Text color={labelColor} fontSize="sm" minW="35px">
                DOB:
              </Text>
              <Text
                color={
                  missingFields.has('date_of_birth') ? missingColor : textColor
                }
                fontSize="sm"
                fontWeight="500"
              >
                {customer.date_of_birth
                  ? formatDate(customer.date_of_birth)
                  : 'Missing'}
              </Text>
            </Flex>
          </Flex>
          <Divider mb={2} />

          <Flex mb={3}>
            <Text color={labelColor} fontSize="sm" minW="60px">
              Address:
            </Text>
            <Flex justify="end" flex={1}>
              <Text
                color={hasAddressMissing ? missingColor : textColor}
                fontSize="sm"
                fontWeight="500"
              >
                {customer.full_address || 'Missing'}
              </Text>
            </Flex>
          </Flex>
          <Divider mb={2} />

          <Flex justify="space-between" mb={3}>
            <Text color={labelColor} fontSize="sm">
              Primary Phone
            </Text>
            <Text
              color={
                missingFields.has('phone') && !customer.mobile_phone?.trim()
                  ? missingColor
                  : textColor
              }
              fontSize="sm"
              fontWeight="500"
            >
              {customer.home_phone || '-'}
            </Text>
          </Flex>

          <Flex justify="space-between" mb={3}>
            <Text color={labelColor} fontSize="sm">
              Mobile:
            </Text>
            <Text
              color={
                missingFields.has('phone') && !customer.home_phone?.trim()
                  ? missingColor
                  : textColor
              }
              fontSize="sm"
              fontWeight="500"
            >
              {customer.mobile_phone || '-'}
            </Text>
          </Flex>

          <Flex justify="space-between" mb={5}>
            <Text color={labelColor} fontSize="sm">
              Email:
            </Text>
            <Text
              color={missingFields.has('email') ? missingColor : textColor}
              fontSize="sm"
              fontWeight="500"
            >
              {customer.email || 'Missing'}
            </Text>
          </Flex>
          <Divider mb={2} />

          <Flex direction="column" gap={3} pt={2}>
            <Button
              {...(customer.mobile_phone?.trim() || customer.home_phone?.trim()
                ? {
                    as: Link,
                    href: `tel:${customer.mobile_phone || customer.home_phone}`,
                  }
                : {})}
              leftIcon={<PhoneIcon />}
              variant="outline"
              colorScheme="blue"
              bg="blue.100"
              size="sm"
              w="100%"
              isDisabled={
                !customer.mobile_phone?.trim() && !customer.home_phone?.trim()
              }
              textTransform="uppercase"
              fontSize="xs"
              fontWeight="600"
            >
              Call Client
            </Button>
            <Button
              {...(customer.email?.trim()
                ? { as: Link, href: `mailto:${customer.email}` }
                : {})}
              rightIcon={<ExternalLinkIcon />}
              bg="#E8683A"
              color="white"
              size="sm"
              w="100%"
              isDisabled={!customer.email?.trim()}
              _hover={{ bg: '#D55A2F', textDecoration: 'none' }}
              _disabled={{ bg: 'gray.300', cursor: 'not-allowed' }}
              textTransform="uppercase"
              fontSize="xs"
              fontWeight="600"
            >
              Send Client Email
            </Button>
          </Flex>
        </Box>
      );
    };

    return (
      <Box
        flex="1"
        maxW={{ base: '100%', lg: '380px' }}
        alignSelf="flex-start"
        bg={cardBg}
        borderRadius="20px"
        overflow="hidden"
        boxShadow="sm"
      >
        <Flex
          bg="linear-gradient(135deg, #E8683A 0%, #F47B4A 100%)"
          px={5}
          py={4}
          justify="space-between"
          align="center"
        >
          <Text
            color="white"
            fontSize="sm"
            fontWeight="700"
            letterSpacing="wider"
            textTransform="uppercase"
          >
            Client Card
          </Text>
          {!isLoading && !error && customer && !isEditing && (
            <Button
              size="xs"
              variant="outline"
              color="white"
              borderColor="white"
              leftIcon={<EditIcon boxSize={3} />}
              onClick={handleEditClick}
              _hover={{ bg: 'whiteAlpha.200' }}
              fontSize="xs"
              fontWeight="600"
            >
              Edit Client
            </Button>
          )}
          {isEditing && (
            <Flex gap={2}>
              <IconButton
                aria-label="Save"
                icon={<Icon as={FaFloppyDisk} />}
                size="xs"
                colorScheme="whiteAlpha"
                bg="whiteAlpha.300"
                color="white"
                onClick={handleSaveEdit}
                isLoading={isUpdating}
                _hover={{ bg: 'whiteAlpha.400' }}
              />
              <IconButton
                aria-label="Reset"
                icon={<RepeatIcon />}
                size="xs"
                variant="outline"
                color="white"
                borderColor="white"
                onClick={handleResetEdit}
                _hover={{ bg: 'whiteAlpha.200' }}
              />
              <IconButton
                aria-label="Close"
                icon={<CloseIcon />}
                size="xs"
                variant="outline"
                color="white"
                borderColor="white"
                onClick={handleCancelEdit}
                _hover={{ bg: 'whiteAlpha.200' }}
              />
            </Flex>
          )}
        </Flex>

        <Box bg="gray.200" py={3} px={5}>
          {isLoading ? (
            <Skeleton height="20px" width="180px" mx="auto" />
          ) : (
            <Text
              color="gray.700"
              fontSize="sm"
              fontWeight="600"
              textAlign="center"
            >
              {COMPANY_NAME}
            </Text>
          )}
        </Box>

        {renderContent()}
      </Box>
    );
  },
);

ClientCard.displayName = 'ClientCard';

export default ClientCard;
